import { AlternateIndicator, SpecificityIndicator } from '../../Indicators';
import { BuildTableData } from '../types';
import { ExternalLink } from '@lifeomic/chromicons';
import { FormattedMessage } from 'react-intl';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { sharedMessages } from '../../sharedIntlLabels';
import { TableConfiguration } from '@lifeomic/chroma-react/components/TableModule';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import {
  buildGnomadUrlFromVariant,
  sangerUrlFromVariant,
} from '../../tableUtils';
import * as React from 'react';
import urls from '../../../../../src/util/urls';
import { getReadSetFileName } from '../../useFetchReadSetFiles';
import { getAminoAcidField } from '../../utils';

// #region Value getters

export const getCKB = (value: any) => {
  if (!value.jaxKnowledge) {
    return null;
  }

  return <SpecificityIndicator variant="short" match={value.jaxKnowledge} />;
};

export const getGene = (value: any) => {
  const gene = getGeneTextOnly(value);
  return (
    <Link to={urls.ncbi.gene(gene)} onClick={e => e.stopPropagation()} newTab>
      {gene}
    </Link>
  );
};

export const getGeneTextOnly = (value: any) => {
  const gene = value?.ensemblCanon?.[0]?.gene || value?.ensembl?.[0]?.gene;

  return gene;
};

export const getAAChange = (value: any, aminoAcidField: string) => {
  const { ensembl, ensemblCanon } = value;

  // No canonical and no alternate transcripts
  if (!ensemblCanon && !ensembl) {
    return null;
  }

  // We don't have a canonical transcript, but we have alternate transcripts.
  // In that case, we can display the indicator with the amino acid changes,
  // after removing duplicates
  if (!ensemblCanon && ensembl && ensembl.length > 0) {
    const dedupedAAChanges = [
      ...new Set(ensembl.map((e: any) => e[aminoAcidField])),
    ].filter(Boolean);

    if (dedupedAAChanges.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Tooltip title={dedupedAAChanges.join('|')} placement="right">
          {/* <span> here is required as a tooltip needs a ref */}
          <span>
            <AlternateIndicator />
          </span>
        </Tooltip>
      </div>
    );
  }

  const [canonical] = ensemblCanon;

  // If we do not have any alternate transcripts, just display
  // the amino acid change from the canonical transcript
  if (!ensembl) {
    return canonical[aminoAcidField];
  }

  // We have alternate transcripts, so we need to do the following:
  // 1) Find the unique amino acid changes by filtering out the
  //    canonical amino acid change from the alternate transcripts'
  //    amino acid changes
  // 2) Dedupe the filtered alternate transcript amino acid changes so that
  //    duplicates don't appear
  const dedupedAAChanges = [
    ...new Set(
      ensembl
        .map((e: any) => {
          if (e[aminoAcidField] === canonical[aminoAcidField]) {
            return null;
          }

          return e[aminoAcidField];
        })
        .filter(Boolean),
    ),
  ];

  // After de-duping and filtering, if we don't have any additional
  // amino acid changes, it's safe to just return the canonical
  // amino acid change as the result...
  if (dedupedAAChanges.length === 0) {
    return canonical[aminoAcidField];
  }

  // ...Otherwise, we have amino acid changes we need to display alongside
  // the amino acid change of the canonical transcript
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 2,
      }}
    >
      <span>{canonical[aminoAcidField]}</span>
      <Tooltip title={dedupedAAChanges.join('|')} placement="right">
        {/* <span> here is required as a tooltip needs a ref */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <AlternateIndicator />
        </div>
      </Tooltip>
    </div>
  );
};

export const getAAChangeTextOnly = (value: any, aminoAcidField: string) => {
  return value?.ensemblCanon?.[0]?.[aminoAcidField];
};

export const getAAChangesTextOnly = (value: any, aminoAcidField: string) => {
  return value?.ensembl
    ?.filter((t: any) =>
      Object.prototype.hasOwnProperty.call(t, aminoAcidField),
    )
    .map((t: any) => t[aminoAcidField]);
};

export const getNNChange = (value: any) =>
  value?.ensemblCanon?.[0]?.nucleotideChange;

export const getPosition = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  build, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
}) => {
  const { position, chromosome } = value;
  if (!position) {
    return null;
  }

  if (!chromosome) {
    return position;
  }

  if (!build) {
    return (
      <FormattedMessage
        id="omicsExplorer.shortVariantsContent.table.position.no-build-value"
        defaultMessage="{chromosome}:{position}"
        values={{
          chromosome,
          position,
        }}
      />
    );
  }

  return (
    <Link
      to={urls.ucsc.genome(build, chromosome, position)}
      onClick={e => e.stopPropagation()}
      newTab
    >
      <FormattedMessage
        id="omicsExplorer.shortVariantsContent.table.position.value"
        defaultMessage="{chromosome}:{position}"
        values={{
          chromosome,
          position,
        }}
      />
    </Link>
  );
};

export const getVariantClass = (value: any) => {
  const { ensembl, ensemblCanon } = value;

  // No canonical and no alternate transcripts
  if (!ensemblCanon && !ensembl) {
    return null;
  }

  // We don't have a canonical transcript, but we have alternate transcripts.
  // We will iterate over the alternate transcripts, determine their
  // classes (note that "class" can actually contain multiple types),
  // dedupe them, and then display them with our indicator + tooltip
  if (!ensemblCanon && ensembl?.length > 0) {
    const dedupedAlternateClasses = [
      ...new Set(
        ensembl
          .map((e: any) => e.class)
          .filter(Boolean)
          .flat(),
      ),
    ];

    if (dedupedAlternateClasses.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Tooltip title={dedupedAlternateClasses.join('|')} placement="right">
          {/* <span> here is required as a tooltip needs a ref */}
          <span>
            <AlternateIndicator />
          </span>
        </Tooltip>
      </div>
    );
  }

  const [canonical] = ensemblCanon;

  // We have alternate transcripts, so we need to do the following:
  // 1) Find the variant "class" by filtering out the
  //    canonical class from the alternate transcripts' classes
  // 2) Dedupe and combine the filtered alternate transcript classes so that
  //    duplicates don't appear
  if (canonical && ensembl?.length > 0) {
    const dedupedAlternateClasses = [
      ...new Set(
        ensembl
          .map((e: any) => {
            if (!e.class) {
              return null;
            }

            if (e.class === canonical.class) {
              return null;
            }

            return e.class;
          })
          .filter(Boolean),
      ),
    ];

    if (dedupedAlternateClasses.length === 0) {
      return canonical.class;
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2,
        }}
      >
        <span>{canonical.class}</span>
        <Tooltip title={dedupedAlternateClasses.join('|')} placement="right">
          {/* <span> here is required as a tooltip needs a ref */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <AlternateIndicator />
          </div>
        </Tooltip>
      </div>
    );
  }

  return canonical.class;
};

export const getVariantClassTextOnly = (value: any) =>
  value?.ensemblCanon?.[0]?.class;

export const getVariantAlleleFreq = (value: any) => {
  // we only want to pad if it's less than one
  if (
    value?.vcf?.variantAllelicFrequency &&
    value?.vcf?.variantAllelicFrequency < 1
  ) {
    return value?.vcf?.variantAllelicFrequency?.toString().padEnd(6, '0');
  }

  return value?.vcf?.variantAllelicFrequency;
};

export const getZygosity = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
}) => {
  const { zygosity } = value;

  if (!zygosity) {
    return null;
  }

  if (zygosity.toLowerCase() === 'heterozygous') {
    return formatMessage(sharedMessages.heterozygous);
  }

  if (zygosity.toLowerCase() === 'homozygous') {
    return formatMessage(sharedMessages.homozygous);
  }

  if (zygosity.toLowerCase() === 'reference') {
    return formatMessage(sharedMessages.heterozygousReference);
  }

  return null;
};

export const getProteinEffect = (value: any) => value?.proteinEffect;

export const getGenomesFreq = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  build, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
}) => {
  const { gnomadAlleleFrequency } = value;

  if (!gnomadAlleleFrequency) {
    return null;
  }

  return buildGnomadUrlFromVariant({
    variant: value,
    displayValue: gnomadAlleleFrequency,
    referenceSetId: build,
  });
};

export const getGenomesHom = (value: any) => value?.gnomadHomozygous;

export const getExomesFreq = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  build, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
}) => {
  const { exacAlleleFrequency } = value;

  if (!exacAlleleFrequency) {
    return null;
  }

  return buildGnomadUrlFromVariant({
    variant: value,
    displayValue: exacAlleleFrequency,
    referenceSetId: build,
  });
};

export const getExomesHom = (value: any) => value?.exacHomozygous;

export const getDbsnpRsId = (value: any) => {
  const { rsid } = value;

  if (!rsid) {
    return null;
  }

  return (
    <Link to={urls.ncbi.rsid(rsid)} onClick={e => e.stopPropagation()} newTab>
      {rsid}
    </Link>
  );
};

export const getClinVarSignificance = (value: any) => {
  const { clinvar } = value;
  if (!clinvar) {
    return null;
  }

  const { significance, alleleId } = clinvar;

  if (!significance) {
    return null;
  }

  if (!alleleId) {
    return significance;
  }

  return (
    <Link
      to={urls.ncbi.clinvar(alleleId)}
      onClick={e => e.stopPropagation()}
      newTab
    >
      {significance}
    </Link>
  );
};

export const getClinVarDisease = (value: any) => value?.clinvar?.disease;

export const getCosmicStatus = (value: any) => {
  const { cosmic } = value;

  if (!cosmic) {
    return null;
  }

  const { status } = value.cosmic;

  if (!status) {
    return null;
  }

  const sangerUrl = sangerUrlFromVariant(value);

  if (!sangerUrl) {
    return status;
  }

  return (
    <Link to={sangerUrl} onClick={e => e.stopPropagation()} newTab>
      {status}
    </Link>
  );
};

export const getCosmicCount = (value: any) => value?.cosmic?.sampleCount;

export const getDamagingPercentage = (value: any) => {
  const { dbnsfp } = value;

  if (!dbnsfp) {
    return null;
  }

  const { damagingCount, damagingPredictor, damagingVote } = dbnsfp;

  if (!damagingVote) {
    return null;
  }

  return `${Math.round(damagingVote * 100)}% (${damagingCount || 0}/${
    damagingPredictor || 0
  })`;
};

export const getDamagingRankScore = (value: any) => {
  const { dbnsfp } = value;

  if (!dbnsfp) {
    return null;
  }

  const { meanRankScore, meanRankScorePredictor } = dbnsfp;

  if (!meanRankScore) {
    return null;
  }

  return `${meanRankScore || 0.0} (${meanRankScorePredictor || 0})`;
};

export const getSift = (value: any) => {
  const { dbnsfp } = value;

  if (!dbnsfp) {
    return null;
  }

  const { siftPred } = dbnsfp;

  if (!siftPred) {
    return null;
  }

  return siftPred.join(',');
};

export const getMutTaster = (value: any) => {
  const { dbnsfp } = value;

  if (!dbnsfp) {
    return null;
  }

  const { mutationTasterPred } = dbnsfp;

  if (!mutationTasterPred) {
    return null;
  }

  return mutationTasterPred.join(',');
};

export const getFathmm = (value: any) => {
  const { dbnsfp } = value;

  if (!dbnsfp) {
    return null;
  }

  const { fathmmPred } = dbnsfp;

  if (!fathmmPred) {
    return null;
  }

  return fathmmPred.join(',');
};

export const getGeneId = (value: any) => {
  const geneId = value?.ensemblCanon?.[0]?.geneId;

  if (!geneId) {
    return null;
  }

  return (
    <Link
      to={urls.ensemble.gene(geneId)}
      onClick={e => e.stopPropagation()}
      newTab
    >
      {geneId}
    </Link>
  );
};

export const getTranscriptId = (value: any) => {
  const transcriptId = value?.ensemblCanon?.[0]?.transcriptId;

  if (!transcriptId) {
    return null;
  }

  return (
    <Link
      to={urls.ensemble.transcript(transcriptId)}
      onClick={e => e.stopPropagation()}
      newTab
    >
      {transcriptId}
    </Link>
  );
};

export const getGeneClass = (value: any) => {
  const ensemblCanonGeneClass = value?.ensemblCanon?.[0]?.biotype;
  const ensemblGeneClasses = value?.ensembl
    ?.map((v: any) => v?.biotype)
    // Filter out ensemblCanon biotype(gene class) so we dont' render it
    // in the alternate transcripts tooltip
    ?.filter((v: any) => v !== ensemblCanonGeneClass);
  const geneClasses = new Set(ensemblGeneClasses);

  const GeneClassesTooltip = () => (
    <Tooltip title={[...geneClasses].join('|')} placement="right">
      <span>
        <AlternateIndicator />
      </span>
    </Tooltip>
  );

  // No canonical and no alternate transcripts
  if (!ensemblCanonGeneClass && !geneClasses.size) {
    return null;
  }

  // No canonical but has alternate transcripts
  if (!ensemblCanonGeneClass && geneClasses.size) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <GeneClassesTooltip />
      </div>
    );
  }

  // Canonical but no alternate transcripts
  if (!geneClasses.size) {
    return ensemblCanonGeneClass;
  }

  // Canonical and alternate transcripts
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }}
    >
      {ensemblCanonGeneClass}
      <GeneClassesTooltip />
    </div>
  );
};

export const getRef = (value: any) => value?.reference;

export const getAlt = (value: any) => value?.alternate;

export const getRefAltDepth = (value: any) => value?.vcf?.coverage;

export const getVcfQual = (value: any) => value?.vcf?.quality;

export const getVcfFilter = (value: any) => value?.vcf?.filter;

export const getCodingEffect = (value: any) => value?.ensemblCanon?.[0]?.group;

export const getExonNumber = (value: any) =>
  value?.ensemblCanon?.[0]?.exonIntronRank;

export const getImpact = (value: any) => value?.ensemblCanon?.[0]?.impact;

export const getMinFreq = (value: any) => value?.minimumAlleleFrequency;

export const getMaxFreq = (value: any) => value?.maximumAlleleFrequency;

export const getClinVarId = (value: any) => {
  const alleleId = value?.clinvar?.alleleId;

  if (!alleleId) {
    return null;
  }

  return (
    <Link
      to={urls.ncbi.clinvar(alleleId)}
      onClick={e => e.stopPropagation()}
      newTab
    >
      {alleleId}
    </Link>
  );
};

export const getClinVarReview = (value: any) => value?.clinvar?.review;

export const getClinVarSubmission = (value: any) => value?.clinvar?.submission;

export const getClinVarNearby = (value: any) => value?.clinvar?.nearVariant;

export const getCosmicId = (value: any) => {
  const { cosmicId } = value.cosmic;

  if (!cosmicId) {
    return null;
  }

  const sangerUrl = sangerUrlFromVariant(value);

  if (!sangerUrl) {
    return cosmicId;
  }

  return (
    <Link to={sangerUrl} onClick={e => e.stopPropagation()} newTab>
      {cosmicId}
    </Link>
  );
};

export const getCosmicHistology = (value: any) => value?.cosmic?.histology;

export const getCosmicSite = (value: any) => value?.cosmic?.tumorSite;

export const getCosmicNearby = (value: any) => value?.cosmic?.nearVariant;

export const getIgv = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  account,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  activeSequence,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  classes,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  variantSet,
}) => {
  const { chromosome, position } = value;
  const choromosomeValue = chromosome.replace('chr', '');

  if (!choromosomeValue || !activeSequence || !activeSequence.sets) {
    return null;
  }

  if (!position && !Number.isInteger(position)) {
    return null;
  }

  const startPos = position - 50;
  const endPos = position + 50;
  const location = `${choromosomeValue}:${startPos}-${endPos}`;
  const readSets = activeSequence.sets.filter(
    (s: any) =>
      s.setType === 'read' && s.sequenceType === variantSet.sequenceType,
  );

  // Our `id` props can sometimes contain other text
  // concatenated to the end of our id. To build up our
  // link, we only care about the guid
  const resultArrayOrNull = new RegExp(
    /(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}/g,
  ).exec(variantSet.id);

  if (!resultArrayOrNull) {
    return null;
  }

  const [variantSetId] = resultArrayOrNull;

  const url = urls.app.genomeViewer({
    account,
    location,
    genome: activeSequence?.referenceSetId,
    variantSet: variantSetId,
    readSets: readSets.flatMap((r: any) => [
      r.id,
      `${r.name} - ${getReadSetFileName(r)}`,
    ]),
  });

  return (
    <IconButtonLink
      aria-label={formatMessage(sharedMessages.viewIgv)}
      className={classes?.innerIconButton}
      paddingTop={0}
      paddingBottom={0}
      paddingLeft={0}
      paddingRight={0}
      icon={ExternalLink}
      size={0}
      to={url}
      target="_blank"
      rel="noopener noreferrer"
      // Stop propagation so the table-row click handler
      // doesn't trigger
      onClick={e => e.stopPropagation()}
    />
  );
};

// #endregion

export const buildTableData = ({
  variantSet,
  activeSequence,
  account,
  build,
  fetchPrependedColumns,
  classes,
  formatMessage,
  onSort,
}: BuildTableData): Array<TableConfiguration> => {
  const aminoAcidField = getAminoAcidField(account);
  const prependedColumns = fetchPrependedColumns
    ? fetchPrependedColumns(variantSet, activeSequence)
    : [];
  return [
    ...prependedColumns,
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.jax),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['jaxKnowledge'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCKB(value),
      },
      isSticky: true,
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.gene),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: [x => x?.ensemblCanon?.[0]?.gene || x?.ensembl?.[0]?.gene],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getGene(value),
      },
      isSticky: true,
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.aaChange),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: [(x: any) => x?.ensemblCanon?.[0]?.[aminoAcidField]],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getAAChange(value, aminoAcidField),
      },
      isSticky: true,
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.nnChange),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].nucleotideChange'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getNNChange(value),
      },
      isSticky: true,
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.position),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['chromosome' || 25, 'position'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection, sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getPosition({ build, value }),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.variantClass),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].class'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getVariantClass(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.variantAlleleFreq),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['vcf.variantAllelicFrequency'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getVariantAlleleFreq(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.zygosity),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['zygosity'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getZygosity({ formatMessage, value }),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.labReportedSignificance),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['vcf.vendSig'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => value?.vcf?.vendSig,
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.genomesFreq),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['gnomadAlleleFrequency'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getGenomesFreq({ build, value }),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.genomesHom),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['gnomadHomozygous'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getGenomesHom(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.exomesFreq),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['exacAlleleFrequency'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getExomesFreq({ build, value }),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.exomesHom),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['exacHomozygous'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getExomesHom(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.dbsnpRsId),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['rsid'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getDbsnpRsId(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.proteinEffect),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['proteinEffect'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getProteinEffect(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.clinVarSignificance),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['clinvar.significance'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getClinVarSignificance(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.clinVarDisease),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['clinvar.disease'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getClinVarDisease(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.cosmicStatus),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['cosmic.status'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCosmicStatus(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.cosmicCount),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['cosmic.sampleCount'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCosmicCount(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.damagingPercentage),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['dnbsfp.damagingVote'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getDamagingPercentage(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.damagingRankScore),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['dnbsfp.meanRankScore'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getDamagingRankScore(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.sift),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['dnbsfp.siftPred'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getSift(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.mutTaster),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['dnbsfp.mutationTasterPred'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getMutTaster(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.fathmm),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['dnbsfp.fathmmPred'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getFathmm(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.geneId),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].geneId'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getGeneId(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.transcriptId),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].transcriptId'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getTranscriptId(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.geneClass),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].biotype'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getGeneClass(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.ref),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['reference'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getRef(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.alt),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['alternate'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getAlt(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.refAltDepth),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['vcf.coverage'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getRefAltDepth(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.vcfQual),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['vcf.quality'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getVcfQual(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.vcfFilter),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['vcf.filter'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getVcfFilter(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.codingEffect),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].group'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCodingEffect(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.exonNumber),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].exonIntronRank'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getExonNumber(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.impact),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['ensemblCanon[0].impact'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getImpact(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.minFreq),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['minimumAlleleFrequency'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getMinFreq(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.maxFreq),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['maximumAlleleFrequency'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getMaxFreq(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.clinVarId),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['clinvar.alleleId'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getClinVarId(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.clinVarReview),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['clinvar.review'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getClinVarReview(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.clinVarSubmission),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['clinvar.submission'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getClinVarSubmission(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.clinVarNearby),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['clinvar.nearVariant'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getClinVarNearby(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.cosmicId),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['cosmic.cosmicId'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCosmicId(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.cosmicHistology),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['cosmic.histology'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCosmicHistology(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.cosmicSite),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['cosmic.tumorSite'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCosmicSite(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.cosmicNearby),
        onSort: ({ sortDirection }) => {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          onSort({
            keys: ['cosmic.nearVariant'],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            directions: sortDirection ? [sortDirection] : null,
          });
        },
      },
      cell: {
        content: value => getCosmicNearby(value),
      },
    },
    {
      header: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        label: formatMessage(sharedMessages.igv),
      },
      cell: {
        content: value =>
          getIgv({
            account,
            activeSequence,
            classes,
            value,
            variantSet,
            formatMessage,
          }),
      },
    },
  ];
};
